
import React, { useEffect, useState } from "react";
import request from "../../service/request";
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import * as yup from 'yup';
import Select from 'react-select'; // Import React Select
import { Stepper, Step, StepLabel } from '@material-ui/core';

const steps = ['Personal Details', 'Additional Information', 'Review & Submit'];

const scrollToTop = () => {
    window.scrollTo({
        top: 0,
        behavior: "smooth",
    });
};

const InquiryForm = () => {
    const [menu, setMenu] = useState([]);
    const [config, setConfig] = useState({});
    const [dropdowns, setDropdowns] = useState({
        property_types: [],
        property_fors: [],
        property_sources: [],
        available_fors: [],
        furnished_statuses: [],
        agents: [],
        property_configurations: [],
        specific_properties: [],
        building_types: [],
        building_statuses: [],
        countries: [],
        states: [],
        cities: [],
        areas: [],
        area_measurements: [],
        projects: [],
        facings: [],
        loan_requirements: [],
        land_types: [],
        mobile_codes: [],
        defaults: {}
    });
    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

    // State for dependent dropdowns
    const [filteredSpecificProperties, setFilteredSpecificProperties] = useState([]);
    const [filteredPropertyConfigs, setFilteredPropertyConfigs] = useState([]);
    const [filteredCities, setFilteredCities] = useState([]);
    const [filteredAreas, setFilteredAreas] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch Config
                const configRes = await request({ method: 'get', url: 'get-inquiry-form-config' });
                if (configRes.data.code === 1 || configRes.data.code === 0) {
                    setConfig(configRes.data.data);
                }

                // Fetch Dropdowns
                const dropdownRes = await request({ method: 'get', url: 'get-inquiry-dropdowns' });
                if (dropdownRes.data.code === 1) {
                    const data = dropdownRes.data.data;
                    setDropdowns(data);

                    // Initialize Dependent Dropdowns if defaults exist or pre-filled
                    if (data.defaults?.state_id) {
                        setFilteredCities(data.cities.filter(c => c.state_id == data.defaults.state_id));
                    } else {
                        setFilteredCities(data.cities);
                    }
                    if (data.defaults?.city_id) {
                        setFilteredAreas(data.areas.filter(a => a.city_id == data.defaults.city_id));
                    } else {
                        setFilteredAreas(data.areas);
                    }
                }

            } catch (error) {
                console.error("Error fetching data:", error);
                setSubmitStatus({ type: 'danger', message: 'Failed to load form data. Please refresh.' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        scrollToTop();
    }, []);

    useEffect(() => {
        request({
            method: 'get',
            url: '/get-menu',
            headers: { "Content-Type": "application/json", "Accept": "application/json" },
        })
            .then((response) => {
                setMenu(response?.data);
            })
            .catch(({ data }) => ({ error: data }));
    }, []);

    const phoneRegExp = /^[0-9]{8,12}$/;

    // Multi-select fields list
    const multiSelectFields = ['specific_property', 'configuration', 'city', 'area_locality', 'facing', 'furnished_status', 'preferred_project'];

    // Dynamic validation schema
    const validationSchema = yup.lazy(values => {
        let schemaShape = {
            // Static Personal Fields
            ...(config.client_name ? { client_name: yup.string().required(`${config.client_name} is required`).min(2, "Name must be at least 2 characters") } : {}),
            ...(config.client_mobile ? { client_mobile: yup.string().matches(phoneRegExp, 'Phone number must be between 8 and 12 digits').required(`${config.client_mobile} is required`) } : {}),
            ...(config.client_email ? { client_email: yup.string().email("Invalid email").nullable() } : {}),
            ...(config.client_alt_mobile ? { client_alt_mobile: yup.string().matches(phoneRegExp, 'Phone number must be between 8 and 12 digits').nullable() } : {}),
        };

        const requiredFields = ['property_type', 'property_for', 'assign_to'];

        Object.keys(config).forEach(key => {
            if (['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key)) return;

            if (requiredFields.includes(key)) {
                if (multiSelectFields.includes(key)) {
                    schemaShape[key] = yup.array().min(1, `${config[key]} is required`).nullable();
                } else {
                    schemaShape[key] = yup.string().required(`${config[key]} is required`);
                }
            } else {
                if (multiSelectFields.includes(key)) {
                    schemaShape[key] = yup.array().nullable();
                } else {
                    schemaShape[key] = yup.string().nullable();
                }
            }
        });

        // Custom Validation for Range Fields
        const greaterThanFrom = (fromKey, label) => {
            return function (val) {
                const fromVal = this.parent[fromKey];
                if (!fromVal || !val) return true;
                return parseFloat(val) > parseFloat(fromVal);
            };
        };

        const notOnlyZero = (msg) => {
            return (val) => !val || parseFloat(val) > 0;
        };

        if (config.area_size_from) schemaShape.area_size_from = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).nullable();
        if (config.area_size_to) schemaShape.area_size_to = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).test('greater', 'Area To must be greater than From', greaterThanFrom('area_size_from')).nullable();

        if (config.budget_from) schemaShape.budget_from = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).nullable();
        if (config.budget_to) schemaShape.budget_to = yup.string().test('not-zero', 'Value must be greater than 0', notOnlyZero()).test('greater', 'Budget To must be greater than From', greaterThanFrom('budget_from')).nullable();

        return yup.object().shape(schemaShape);
    });

    const formik = useFormik({
        initialValues: {
            client_name: '',
            client_mobile: '',
            client_mobile_code: '+91',
            client_email: '',
            client_alt_mobile: '',
            client_alt_mobile_code: '+91',
            property_type: '',
            property_for: '',
            inquiry_source: '',
            preferred_project: [],
            facing: [],
            land_type: '',
            loan_requirements: '',
            area_measurement: dropdowns.area_measurements?.[0]?.id || 1,
            // Initialize others
            ...Object.keys(config).reduce((acc, key) => {
                if (!['client_name', 'client_mobile', 'client_email', 'client_alt_mobile', 'property_type', 'property_for', 'inquiry_source', 'preferred_project', 'facing', 'land_type', 'loan_requirements', 'area_measurement'].includes(key)) {
                    if (multiSelectFields.includes(key)) {
                        acc[key] = [];
                    } else {
                        acc[key] = '';
                    }
                }
                return acc;
            }, {})
        },
        enableReinitialize: true,
        validationSchema: validationSchema,
        onSubmit: (values, { resetForm }) => {
            if (activeStep !== steps.length - 1) {
                return;
            }
            setSubmitStatus({ type: '', message: '' });
            request({
                method: 'post',
                url: 'inquiry/store-from-form',
                data: values
            })
                .then((response) => {
                    if (response.data.code === 0 || response.data.code === 'success') {
                        setSubmitStatus({ type: 'success', message: 'Inquiry saved successfully!' });
                        resetForm();
                        setActiveStep(0);
                    } else {
                        setSubmitStatus({ type: 'danger', message: response.data.message || 'Failed to save inquiry' });
                    }
                })
                .catch((error) => {
                    setSubmitStatus({ type: 'danger', message: 'An error occurred. Please try again.' });
                });
        }
    });

    // Handle dependent field changes
    const handleFieldChange = (key, value) => {
        formik.setFieldValue(key, value);

        if (key === 'property_type') {
            const pType = parseInt(value);
            const specificProps = dropdowns.specific_properties.filter(item => item.property_type_id === pType);
            const propConfigs = dropdowns.property_configurations.filter(item => item.property_type_id === pType);

            setFilteredSpecificProperties(specificProps);
            setFilteredPropertyConfigs(propConfigs);

            formik.setFieldValue('specific_property', []);
            formik.setFieldValue('configuration', []);
        }

        if (key === 'state') {
            const stateId = parseInt(value);
            const cities = dropdowns.cities.filter(item => item.state_id === stateId);
            setFilteredCities(cities);
            setFilteredAreas([]);
            formik.setFieldValue('city', []);
            formik.setFieldValue('area_locality', []);
        }

        if (key === 'city') {
            const cityIds = Array.isArray(value) ? value : [value];
            const areas = dropdowns.areas.filter(item => cityIds.includes(item.city_id));
            setFilteredAreas(areas);

            if (formik.values.area_locality) {
                const currentLocIds = formik.values.area_locality || [];
                const validLocIds = currentLocIds.filter(id => areas.some(a => a.id === id));
                formik.setFieldValue('area_locality', validLocIds);
            }
        }
    };

    const handleNext = async () => {
        let stepFields = [];
        if (activeStep === 0) {
            if (config.client_name) stepFields.push('client_name');
            if (config.client_mobile) stepFields.push('client_mobile');
            if (config.client_email) stepFields.push('client_email');
            if (config.client_alt_mobile) stepFields.push('client_alt_mobile');
        } else if (activeStep === 1) {
            stepFields = Object.keys(config).filter(key => config[key] && !['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key));
        }

        const touchedFields = {};
        stepFields.forEach(field => { touchedFields[field] = true; });
        await formik.setTouched({ ...formik.touched, ...touchedFields });

        const errors = await formik.validateForm();
        const stepHasErrors = stepFields.some(field => errors[field]);

        if (!stepHasErrors) {
            setActiveStep((prev) => prev + 1);
        }
    };

    const handleBack = () => {
        setSubmitStatus({ type: '', message: '' });
        setActiveStep((prev) => prev - 1);
    };

    const renderField = (key, label, isRequired) => {
        let options = [];
        let isSelect = false;

        switch (key) {
            case 'property_type': isSelect = true; options = dropdowns.property_types; break;
            case 'property_for':
                isSelect = true;
                options = dropdowns.property_fors.map(opt => (opt.name === 'Sell' ? { ...opt, name: 'Buy' } : opt));
                break;
            case 'assign_to': isSelect = true; options = dropdowns.agents; break;
            case 'inquiry_source': isSelect = true; options = dropdowns.property_sources; break;
            case 'available_for': isSelect = true; options = dropdowns.available_fors; break;
            case 'furnished_status': isSelect = true; options = dropdowns.furnished_statuses; break;
            case 'building_type_id': isSelect = true; options = dropdowns.building_types; break;
            case 'building_status': isSelect = true; options = dropdowns.building_statuses; break;
            case 'specific_property': isSelect = true; options = filteredSpecificProperties.length > 0 ? filteredSpecificProperties : dropdowns.specific_properties; break;
            case 'configuration': isSelect = true; options = filteredPropertyConfigs.length > 0 ? filteredPropertyConfigs : dropdowns.property_configurations; break;
            case 'state': isSelect = true; options = dropdowns.states; break;
            case 'city': isSelect = true; options = filteredCities.length > 0 ? filteredCities : (formik.values.state ? [] : dropdowns.cities); break;
            case 'area_locality':
                isSelect = true; options = filteredAreas.length > 0 ? filteredAreas.map(a => ({ id: a.id, name: a.area || a.name })) : (formik.values.city && formik.values.city.length > 0 ? [] : dropdowns.areas.map(a => ({ id: a.id, name: a.area || a.name }))); break;
            case 'preferred_project': isSelect = true; options = dropdowns.projects; break;
            case 'facing': isSelect = true; options = dropdowns.facings; break;
            case 'loan_requirements': isSelect = true; options = dropdowns.loan_requirements; break;
            case 'land_type': isSelect = true; options = dropdowns.land_types; break;

            default: isSelect = false;
        }

        if (isSelect) {
            // Prepare options for React Select {value, label}
            const selectOptions = options.map(opt => ({ value: opt.id, label: opt.name || opt.area || opt.measurement || opt.value })); // Adjust based on data structure

            if (multiSelectFields.includes(key)) {

                // value prop for MultiSelect
                const selectedValue = selectOptions.filter(opt => (formik.values[key] || []).includes(opt.value));

                return (
                    <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                        <label className="text-muted font-weight-bold mb-1 d-block text-left">
                            {label} {isRequired && <span className="text-danger">*</span>}
                        </label>
                        <Select
                            isMulti
                            className="basic-multi-select"
                            classNamePrefix="select"
                            options={selectOptions}
                            value={selectedValue}
                            onChange={(selected) => handleFieldChange(key, selected ? selected.map(s => s.value) : [])}
                            placeholder={`Select ${label}...`}
                            styles={{
                                control: (base) => ({ ...base, minHeight: '38px' }),
                                menu: (base) => ({ ...base, zIndex: 9999 })
                            }}
                        />
                        {formik.touched[key] && formik.errors[key] && (
                            <div className="text-danger small mt-1 ml-1">{formik.errors[key]}</div>
                        )}
                    </div>
                );
            }

            // Single Select Logic
            const selectedValue = selectOptions.find(opt => opt.value == formik.values[key]) || null;

            return (
                <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <Select
                        className="basic-single-select"
                        classNamePrefix="select"
                        options={selectOptions}
                        value={selectedValue}
                        onChange={(selected) => handleFieldChange(key, selected ? selected.value : '')}
                        placeholder={`Select ${label}...`}
                        styles={{
                            control: (base) => ({ ...base, minHeight: '38px' }),
                            menu: (base) => ({ ...base, zIndex: 9999 })
                        }}
                    />
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="text-danger small mt-1 ml-1">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'client_mobile' || key === 'client_alt_mobile') {
            const codeKey = key === 'client_mobile' ? 'client_mobile_code' : 'client_alt_mobile_code';
            return (
                <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group" style={{ width: '100%' }}>
                        <div className="input-group-prepend">
                            <select
                                className="form-control"
                                name={codeKey}
                                value={formik.values[codeKey]}
                                onChange={formik.handleChange}
                                style={{ height: '38px', minWidth: '80px', borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            >
                                {dropdowns.mobile_codes.map(code => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                        </div>
                        <input
                            type="text"
                            name={key}
                            maxLength={12}
                            className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                            placeholder={`Enter ${label}`}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (/^\d*$/.test(val)) {
                                    formik.setFieldValue(key, val);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values[key]}
                            style={{ height: '38px' }}
                        />
                    </div>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback d-block">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'area_size_from' || key === 'area_size_to') {
            return (
                <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <div className="input-group" style={{ width: '100%' }}>
                        <input
                            type="text"
                            name={key}
                            className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                            placeholder={`Enter ${label}`}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val === '' || /^[1-9][0-9]*$/.test(val)) {
                                    formik.setFieldValue(key, val);
                                }
                            }}
                            onBlur={formik.handleBlur}
                            value={formik.values[key]}
                            style={{ height: '38px' }}
                        />
                        <div className="input-group-append">
                            <select
                                className="form-control"
                                name="area_measurement"
                                value={formik.values.area_measurement}
                                onChange={formik.handleChange}
                                style={{ height: '38px', minWidth: '80px', borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            >
                                {dropdowns.area_measurements.map(m => (
                                    <option key={m.id} value={m.id}>{m.measurement}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback d-block">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'budget_from' || key === 'budget_to') {
            return (
                <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <input
                        type="text"
                        name={key}
                        className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                        placeholder={`Enter ${label}`}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val === '' || /^[1-9][0-9]*$/.test(val)) {
                                formik.setFieldValue(key, val);
                            }
                        }}
                        onBlur={formik.handleBlur}
                        value={formik.values[key]}
                        style={{ height: '38px' }}
                    />
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        if (key === 'remarks') {
            return (
                <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                    <label className="text-muted font-weight-bold mb-1 d-block text-left">
                        {label} {isRequired && <span className="text-danger">*</span>}
                    </label>
                    <textarea
                        name={key}
                        rows="1"
                        className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                        placeholder={`Enter ${label}`}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values[key]}
                    ></textarea>
                    {formik.touched[key] && formik.errors[key] && (
                        <div className="invalid-feedback">{formik.errors[key]}</div>
                    )}
                </div>
            );
        }

        return (
            <div className="col-md-6 form-group" key={key} style={{ marginBottom: '20px' }}>
                <label className="text-muted font-weight-bold mb-1 d-block text-left">
                    {label} {isRequired && <span className="text-danger">*</span>}
                </label>
                <input
                    type="text"
                    name={key}
                    className={`form-control ${formik.touched[key] && formik.errors[key] ? 'is-invalid' : ''}`}
                    placeholder={`Enter ${label}`}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values[key]}
                    style={{ height: '38px' }}
                />
                {formik.touched[key] && formik.errors[key] && (
                    <div className="invalid-feedback">{formik.errors[key]}</div>
                )}
            </div>
        );
    };

    const renderStepContent = (step) => {
        switch (step) {
            case 0:
                const step0Fields = ['client_name', 'client_mobile', 'client_alt_mobile', 'client_email'];
                return (
                    <div className="row">
                        {step0Fields.map(key => {
                            if (!config[key]) return null;
                            const isRequired = ['client_name', 'client_mobile'].includes(key);
                            return renderField(key, config[key], isRequired);
                        })}
                    </div>
                );
            case 1:
                const hasFields = Object.keys(config).some(key => config[key] && !['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key));
                if (!hasFields) {
                    return <div className="text-center text-muted">No additional information required.</div>;
                }
                return (
                    <div className="row">
                        {Object.keys(config).map((key) => {
                            if (!config[key] || ['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key)) return null;

                            const isLand = formik.values.property_type == 4;

                            if (key === 'land_type' && !isLand) return null;
                            if ((key === 'budget_from' || key === 'budget_to') && isLand) return null;

                            const isRequired = ['property_type', 'property_for', 'assign_to'].includes(key);
                            return renderField(key, config[key], isRequired);
                        })}
                    </div>
                );
            case 2:
                // Exclude keys from display
                const excludeKeys = ['state_id', 'city_id', 'client_mobile_code', 'client_alt_mobile_code', 'area_measurement'];
                const step0Keys = ['client_name', 'client_mobile', 'client_alt_mobile', 'client_email'];
                const step1Keys = Object.keys(config).filter(key => config[key] && !step0Keys.includes(key));
                const allKeys = [...step0Keys, ...step1Keys];

                return (
                    <div className="text-left">
                        <h5 className="mb-4">Review your details</h5>
                        <div className="row">
                            {allKeys.map((key) => {
                                const value = formik.values[key];

                                if (excludeKeys.includes(key)) return null;
                                if (!value || value === '' || (Array.isArray(value) && value.length === 0)) return null;

                                const isLand = formik.values.property_type == 4;
                                if (key === 'land_type' && !isLand) return null;
                                if ((key === 'budget_from' || key === 'budget_to') && isLand) return null;

                                let label = config[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                                let displayValue = value;

                                // Custom Display Logic
                                if (key === 'client_mobile') {
                                    displayValue = `${formik.values.client_mobile_code} ${value}`;
                                } else if (key === 'client_alt_mobile') {
                                    displayValue = `${formik.values.client_alt_mobile_code} ${value}`;
                                } else if (key === 'area_size_from' || key === 'area_size_to') {
                                    const measId = formik.values.area_measurement;
                                    const measObj = dropdowns.area_measurements.find(m => m.id == measId);
                                    const unit = measObj ? measObj.measurement : '';
                                    displayValue = `${value} ${unit}`;
                                } else if (key === 'budget_from' || key === 'budget_to') {
                                    displayValue = Number(value).toLocaleString('en-IN');
                                }

                                // Single Select mappings
                                if (key === 'property_type') displayValue = dropdowns.property_types.find(i => i.id == value)?.name || value;
                                else if (key === 'property_for') displayValue = dropdowns.property_fors.find(i => i.id == value)?.name || value;
                                else if (key === 'assign_to') displayValue = dropdowns.agents.find(i => i.id == value)?.name || value;
                                else if (key === 'inquiry_source') displayValue = dropdowns.property_sources.find(i => i.id == value)?.name || value;
                                else if (key === 'available_for') displayValue = dropdowns.available_fors.find(i => i.id == value)?.name || value;
                                else if (key === 'building_type_id') displayValue = dropdowns.building_types.find(i => i.id == value)?.name || value;
                                else if (key === 'building_status') displayValue = dropdowns.building_statuses.find(i => i.id == value)?.name || value;
                                else if (key === 'state') displayValue = dropdowns.states.find(i => i.id == value)?.name || value;
                                else if (key === 'preferred_project') displayValue = dropdowns.projects.find(i => i.id == value)?.name || value;
                                else if (key === 'loan_requirements') displayValue = dropdowns.loan_requirements.find(i => i.id == value)?.value || value;
                                else if (key === 'land_type') displayValue = dropdowns.land_types.find(i => i.id == value)?.value || value;

                                // Multi-Select & Dependent mappings
                                if (multiSelectFields.includes(key)) {
                                    const sourceKeyMap = {
                                        'specific_property': 'specific_properties',
                                        'configuration': 'property_configurations',
                                        'city': 'cities',
                                        'area_locality': 'areas',
                                        'facing': 'facings',
                                        'furnished_status': 'furnished_statuses'
                                    };
                                    const sourceKey = sourceKeyMap[key];
                                    if (sourceKey && dropdowns[sourceKey]) {
                                        const ids = Array.isArray(value) ? value : [value];
                                        displayValue = ids.map(id => {
                                            const item = dropdowns[sourceKey].find(i => i.id == id);
                                            return item ? (item.name || item.area || item.measurement || item.value) : id;
                                        }).join(', ');
                                    }
                                }

                                return (
                                    <div className="col-sm-6 mb-3" key={key}>
                                        <small className="text-muted d-block">{label}</small>
                                        <span className="font-weight-bold">{displayValue}</span>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                );
            default:
                return 'Unknown step';
        }
    };

    if (loading) return <div className="text-center py-5">Loading...</div>;

    const additionalFields = Object.keys(config).filter(key => config[key] && !['client_name', 'client_mobile', 'client_email', 'client_alt_mobile'].includes(key));
    const hasAdditionalFields = additionalFields.length > 0;

    return (
        <>
            <Helmet>
                <title>{`${menu.data?.site_setting?.site_name} - Submit Inquiry`}</title>
            </Helmet>
            <main id="main">
                <section className="bg-gray" style={{ paddingTop: "5rem", paddingBottom: "5rem" }}>
                    <div className="container">
                        <div className="row justify-content-center">
                            <div className="col-12 col-lg-10">
                                <div className="card shadow-lg border-0 rounded-lg">
                                    <div className="card-body p-4 p-md-5">
                                        <div className="text-center mb-5">
                                            <h3 style={{ color: "#333", fontWeight: "700" }}>Submit Inquiry</h3>
                                            <p className="text-muted">Please fill in the details below</p>
                                        </div>

                                        <Stepper activeStep={activeStep} alternativeLabel className="mb-4">
                                            {steps.map((label) => (
                                                <Step key={label}>
                                                    <StepLabel>{label}</StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>

                                        {submitStatus.message && (
                                            <div className={`alert alert-${submitStatus.type} shadow-sm border-0 mb-4`} role="alert">
                                                {submitStatus.type === 'success' ? <i className="fa fa-check-circle mr-2"></i> : <i className="fa fa-exclamation-circle mr-2"></i>}
                                                {submitStatus.message}
                                            </div>
                                        )}

                                        <form>
                                            {renderStepContent(activeStep)}

                                            <div className="row mt-5">
                                                <div className="col-12 d-flex justify-content-between">
                                                    <button
                                                        type="button"
                                                        disabled={activeStep === 0}
                                                        onClick={handleBack}
                                                        className="btn-custome text-white px-4"
                                                        style={{ backgroundColor: "#6c757d", borderRadius: ".25rem" }}
                                                    >
                                                        Back
                                                    </button>
                                                    {activeStep === steps.length - 1 ? (
                                                        <button type="button" className="btn text-white px-4" onClick={formik.handleSubmit}>
                                                            Submit Inquiry
                                                        </button>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={handleNext}
                                                            className="btn text-white px-4"
                                                        >
                                                            {activeStep === 1 || !hasAdditionalFields ? "Review" : "Next"}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
        </>
    );
};

export default InquiryForm;
